import { Modale } from '../ui/Modale';
import { Pastille } from '../ui/Primitives';
import { Icone } from '../Icone';
import { PASTILLE_PIECE } from '../../lib/pastilles';
import { useReferentiels } from '../../lib/useDocuments';
import { FicheEntretienLecture } from './FicheEntretienLecture';
import type { Dossier, Mobilite } from '../../lib/useApprenants';
import { libelleOrientation } from '../../lib/useEntretiens';

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
function dateCourte(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function ouiNon(valeur: boolean): string {
  return valeur ? 'Oui' : 'Non';
}

function texte(valeur: string | null | undefined): string {
  return valeur && valeur.trim() !== '' ? valeur : '—';
}

const LIBELLES_MOBILITE: Record<Mobilite | 'VIDE', string> = {
  PERMIS_B: 'Permis B',
  TRANSPORTS_EN_COMMUN: 'Transports en commun',
  AUCUNE: 'Aucune',
  VIDE: '—',
};

interface EntreeNomenclature {
  code: string;
  libelle: string;
}

/**
 * Libellé d'un code de référentiel CERFA. Le code est conservé à côté : c'est
 * lui qui part sur le formulaire officiel, et c'est par lui qu'une valeur se
 * vérifie face à la notice.
 */
function libelleCode(codes: EntreeNomenclature[], code: string | null | undefined): string {
  if (!code) return '—';
  const trouve = codes.find((entree) => entree.code === code);
  return trouve ? `${code} · ${trouve.libelle}` : code;
}

/** Une rubrique de la fiche : intitulé au-dessus, valeur en dessous. */
function Ligne({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {libelle}
      </div>
      <div className="mt-0.5 break-words text-sm font-medium text-codialis-navy">{valeur}</div>
    </div>
  );
}

function Bloc({
  titre,
  children,
}: {
  titre: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-gray-100 bg-white p-4">
      <h3 className="font-display text-sm font-bold text-codialis-navy">{titre}</h3>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

/**
 * Fiche complète du dossier apprenant, en lecture seule (CDC §4.3).
 *
 * L'écran du dossier ne montre que ce qui sert à le faire avancer : une
 * dizaine de rubriques, les pièces et les alertes. Tout le reste — état civil
 * complet, bloc CERFA, parcours scolaire, représentant légal, entreprise et
 * contrat — est saisi puis n'est plus jamais relu, alors qu'il part sur les
 * documents réglementaires. Cette fiche le rassemble d'un seul tenant, pour le
 * vérifier avant de générer un CERFA ou une convention.
 *
 * Ce qui est propre à l'apprentissage n'est pas affiché vide en mode OF : il
 * est masqué, comme partout ailleurs (CDC §2.2), et l'API ne l'envoie même pas.
 */
export function FicheDossier({
  dossier,
  onFermer,
  onVoirCompteRendu,
}: {
  dossier: Dossier;
  onFermer: () => void;
  onVoirCompteRendu?: () => void;
}): JSX.Element {
  const { data: referentiels } = useReferentiels();
  const nomenclatures = referentiels?.cerfaApprenti;
  const cerfa = dossier.cerfa;
  const of = dossier.dossier.mode === 'OF';

  const representant = cerfa?.representant;
  const representantRenseigne =
    representant !== undefined &&
    Object.values(representant).some((valeur) => (valeur ?? '').trim() !== '');

  return (
    <Modale
      titre="Fiche du dossier"
      sousTitre={`${dossier.identite.prenom} ${dossier.identite.nom} · ${
        of ? 'Organisme de Formation' : 'CFA · apprentissage'
      } · antenne ${dossier.dossier.site.code}`}
      ouverte
      onFermer={onFermer}
      largeur="max-w-5xl"
    >
      <div className="space-y-4 bg-codialis-sand/40 px-4 py-5 sm:px-6">
        <Bloc titre="État civil">
          <Ligne libelle={cerfa ? 'Nom de naissance' : 'Nom'} valeur={dossier.identite.nom} />
          {cerfa && <Ligne libelle="Nom d’usage" valeur={texte(cerfa.nomUsage)} />}
          <Ligne libelle="Prénom" valeur={dossier.identite.prenom} />
          <Ligne
            libelle="Date de naissance"
            valeur={
              dossier.identite.dateNaissance
                ? `${dateCourte(dossier.identite.dateNaissance)}${
                    dossier.identite.age === null ? '' : ` (${dossier.identite.age} ans)`
                  }`
                : '—'
            }
          />
          <Ligne
            libelle="Sexe"
            valeur={
              dossier.identite.sexe === 'M'
                ? 'Masculin'
                : dossier.identite.sexe === 'F'
                  ? 'Féminin'
                  : '—'
            }
          />
          <Ligne
            libelle={cerfa ? 'Commune de naissance' : 'Lieu de naissance'}
            valeur={texte(dossier.identite.lieuNaissance)}
          />
          {cerfa && (
            <Ligne
              libelle="Département de naissance"
              valeur={texte(cerfa.departementNaissance)}
            />
          )}
          <Ligne libelle="Nationalité" valeur={texte(dossier.identite.nationalite)} />
          <Ligne libelle="INE" valeur={texte(dossier.identite.ine)} />
          {cerfa && <Ligne libelle="NIR" valeur={texte(cerfa.nir)} />}
          {cerfa && (
            <Ligne
              libelle="Régime social"
              valeur={libelleCode(nomenclatures?.regimesSociaux ?? [], cerfa.regimeSocial)}
            />
          )}
        </Bloc>

        <Bloc titre="Coordonnées">
          {cerfa ? (
            <>
              <Ligne libelle="N° de voie" valeur={texte(cerfa.numeroVoie)} />
              <Ligne libelle="Voie" valeur={texte(cerfa.voie)} />
              <Ligne libelle="Complément" valeur={texte(cerfa.complementAdresse)} />
            </>
          ) : (
            <Ligne libelle="Adresse" valeur={texte(dossier.identite.adresse)} />
          )}
          <Ligne libelle="Code postal" valeur={texte(dossier.identite.codePostal)} />
          <Ligne libelle="Commune" valeur={texte(dossier.identite.ville)} />
          <Ligne libelle="Téléphone" valeur={texte(dossier.identite.telephone)} />
          <Ligne libelle="Courriel" valeur={texte(dossier.identite.email)} />
          {cerfa && (
            <Ligne libelle="Adresse recomposée" valeur={texte(dossier.identite.adresse)} />
          )}
        </Bloc>

        <Bloc titre="Dossier">
          <Ligne
            libelle="Type de formation"
            valeur={of ? 'OF · organisme de formation' : 'CFA · apprentissage'}
          />
          <Ligne libelle="Statut" valeur={dossier.dossier.statutLibelle} />
          <Ligne
            libelle="Antenne"
            valeur={`${dossier.dossier.site.nom} (${dossier.dossier.site.code})`}
          />
          <Ligne
            libelle="Formation"
            valeur={
              dossier.dossier.formation
                ? `${dossier.dossier.formation.titre} · ${dossier.dossier.formation.niveau} · RNCP ${dossier.dossier.formation.rncp}`
                : '—'
            }
          />
          {of && (
            <Ligne
              libelle="Session"
              valeur={
                dossier.dossier.session
                  ? `${dossier.dossier.session.libelle} · ${dateCourte(
                      dossier.dossier.session.dateDebut,
                    )} → ${dateCourte(dossier.dossier.session.dateFin)}`
                  : 'Aucune session rattachée'
              }
            />
          )}
          <Ligne
            libelle="Conseiller référent"
            valeur={
              dossier.dossier.conseiller
                ? `${dossier.dossier.conseiller.prenom} ${dossier.dossier.conseiller.nom}`
                : '—'
            }
          />
          <Ligne libelle="Date d’inscription" valeur={dateCourte(dossier.dossier.dateInscription)} />
          <Ligne libelle="Date d’entrée" valeur={dateCourte(dossier.dossier.dateEntree)} />
          <Ligne libelle="Date de fin prévue" valeur={dateCourte(dossier.dossier.dateFinPrevue)} />
          {!of && (
            <Ligne libelle="Date d’examen" valeur={dateCourte(dossier.dossier.dateExamen)} />
          )}
          <Ligne
            libelle="Carte étudiante des métiers"
            valeur={
              dossier.dossier.carteEtudianteFaite
                ? `Faite${
                    dossier.dossier.carteEtudianteLe
                      ? ` le ${dateCourte(dossier.dossier.carteEtudianteLe)}`
                      : ''
                  }`
                : 'Non'
            }
          />
          <Ligne
            libelle="En recherche d’entreprise"
            valeur={
              dossier.dossier.rechercheEntreprise
                ? `Oui${
                    dossier.dossier.rechercheDepuisLe
                      ? ` · depuis le ${dateCourte(dossier.dossier.rechercheDepuisLe)}`
                      : ''
                  }`
                : 'Non'
            }
          />
          <Ligne libelle="Mobilité" valeur={LIBELLES_MOBILITE[dossier.dossier.mobilite ?? 'VIDE']} />
          <Ligne
            libelle="Espace candidat"
            valeur={
              dossier.dossier.compte
                ? `${dossier.dossier.compte.email}${
                    dossier.dossier.compte.dernierAcces
                      ? ` · dernier accès le ${dateCourte(dossier.dossier.compte.dernierAcces)}`
                      : ' · jamais connecté'
                  }`
                : 'Non créé'
            }
          />
          <Ligne
            libelle="Lien d’accès envoyé"
            valeur={dateCourte(dossier.dossier.lienAccesEnvoyeLe)}
          />
        </Bloc>

        <Bloc titre="Situation">
          <Ligne libelle="Minorité à l’entrée" valeur={ouiNon(dossier.situation.mineur)} />
          <Ligne libelle="RQTH" valeur={ouiNon(dossier.situation.rqth)} />
          <Ligne libelle="Hébergé par un tiers" valeur={ouiNon(dossier.situation.hebergeParTiers)} />
          <Ligne libelle="Déjà apprenti" valeur={ouiNon(dossier.situation.dejaApprenti)} />
          <Ligne
            libelle="Inscrit(e) à France Travail"
            valeur={ouiNon(dossier.situation.demandeurEmploi)}
          />
          <Ligne
            libelle="Titulaire d’un diplôme"
            valeur={ouiNon(dossier.situation.titulaireDiplome)}
          />
          {cerfa && (
            <>
              <Ligne
                libelle="Sportif de haut niveau"
                valeur={ouiNon(cerfa.sportifHautNiveau)}
              />
              <Ligne
                libelle="Projet de création d’entreprise"
                valeur={ouiNon(cerfa.projetCreationEntreprise)}
              />
              <Ligne
                libelle="Équivalence jeunes (à défaut de RQTH)"
                valeur={ouiNon(cerfa.rqthEquivalenceJeunes)}
              />
              <Ligne libelle="Extension BOE" valeur={ouiNon(cerfa.rqthExtensionBoe)} />
            </>
          )}
        </Bloc>

        {cerfa && (
          <Bloc titre="Parcours avant le contrat">
            <Ligne
              libelle="Situation avant ce contrat"
              valeur={libelleCode(
                nomenclatures?.situationsAvantContrat ?? [],
                cerfa.situationAvantContrat,
              )}
            />
            <Ligne
              libelle="Dernier diplôme préparé"
              valeur={libelleCode(nomenclatures?.diplomes ?? [], cerfa.dernierDiplomePrepare)}
            />
            <Ligne
              libelle="Dernière classe suivie"
              valeur={libelleCode(nomenclatures?.dernieresClasses ?? [], cerfa.derniereClasseSuivie)}
            />
            <Ligne
              libelle="Diplôme le plus élevé obtenu"
              valeur={libelleCode(nomenclatures?.diplomes ?? [], cerfa.diplomeLePlusEleve)}
            />
            <Ligne
              libelle="Intitulé précis du dernier diplôme préparé"
              valeur={texte(cerfa.intitulePrecisDernierDiplome)}
            />
          </Bloc>
        )}

        {representant && representantRenseigne && (
          <Bloc titre="Représentant légal">
            <Ligne libelle="Nom" valeur={texte(representant.nom)} />
            <Ligne libelle="Prénom" valeur={texte(representant.prenom)} />
            <Ligne libelle="N° de voie" valeur={texte(representant.numeroVoie)} />
            <Ligne libelle="Voie" valeur={texte(representant.voie)} />
            <Ligne libelle="Complément" valeur={texte(representant.complement)} />
            <Ligne libelle="Code postal" valeur={texte(representant.codePostal)} />
            <Ligne libelle="Commune" valeur={texte(representant.commune)} />
            <Ligne libelle="Courriel" valeur={texte(representant.courriel)} />
          </Bloc>
        )}

        {dossier.entreprise && (
          <Bloc titre="Entreprise d’accueil">
            <Ligne libelle="Dénomination" valeur={dossier.entreprise.denomination} />
            <Ligne libelle="SIRET" valeur={dossier.entreprise.siret} />
            <Ligne libelle="Commune" valeur={texte(dossier.entreprise.ville)} />
            <Ligne libelle="OPCO" valeur={texte(dossier.entreprise.opco)} />
            <Ligne libelle="IDCC" valeur={texte(dossier.entreprise.idcc)} />
          </Bloc>
        )}

        {dossier.tuteur && (
          <Bloc titre="Maître d’apprentissage">
            <Ligne libelle="Nom" valeur={dossier.tuteur.nom} />
            <Ligne libelle="Prénom" valeur={dossier.tuteur.prenom} />
            <Ligne libelle="Emploi occupé" valeur={texte(dossier.tuteur.emploiOccupe)} />
            <Ligne
              libelle="Années d’expérience"
              valeur={
                dossier.tuteur.anneesExperience === null
                  ? '—'
                  : String(dossier.tuteur.anneesExperience)
              }
            />
            <Ligne libelle="Courriel" valeur={texte(dossier.tuteur.email)} />
            <Ligne libelle="Téléphone" valeur={texte(dossier.tuteur.telephone)} />
          </Bloc>
        )}

        {dossier.contrat && (
          <Bloc titre="Contrat d’apprentissage">
            <Ligne libelle="Statut" valeur={dossier.contrat.statut} />
            <Ligne libelle="Modèle de CERFA" valeur={texte(dossier.contrat.modeleCerfa)} />
            <Ligne libelle="Début" valeur={dateCourte(dossier.contrat.dateDebut)} />
            <Ligne libelle="Fin" valeur={dateCourte(dossier.contrat.dateFin)} />
            <Ligne
              libelle="Fin de période d’essai"
              valeur={dateCourte(dossier.contrat.finPeriodeEssai)}
            />
            <Ligne libelle="Échéance des 5 mois" valeur={dateCourte(dossier.contrat.echeance5Mois)} />
          </Bloc>
        )}

        {/* Ce qui a été recueilli pendant l'entretien de recrutement : le
            dossier est le point d'entrée du suivi, la fiche ne reste pas dans
            le seul tableau du pôle commercial. */}
        <section className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display text-sm font-bold text-codialis-navy">
              Entretien de recrutement
              {dossier.entretien ? ` · ${dateCourte(dossier.entretien.dateEntretien)}` : ''}
            </h3>
            {dossier.entretien && onVoirCompteRendu && (
              <button
                type="button"
                onClick={onVoirCompteRendu}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                <Icone nom="document" className="h-3.5 w-3.5 text-codialis-sky" />
                Compte-rendu PDF
              </button>
            )}
          </div>
          {!dossier.entretien ? (
            <p className="mt-2 text-sm text-codialis-gray">
              Aucun entretien de recrutement sur ce dossier. Il se planifie depuis « Entretiens ».
            </p>
          ) : !dossier.entretien.fiche ? (
            <p className="mt-2 text-sm text-codialis-gray">
              L’entretien est planifié, sa fiche n’a pas encore été remplie.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              <FicheEntretienLecture
                fiche={dossier.entretien.fiche}
                referentiel={dossier.referentielFiche}
              />
              {dossier.entretien.compteRendu && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                    Compte-rendu du chargé de recrutement
                  </div>
                  <p className="mt-0.5 whitespace-pre-line text-sm font-medium text-codialis-navy">
                    {dossier.entretien.compteRendu}
                  </p>
                </div>
              )}
              {dossier.entretien.besoinsRecueillis && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                    Besoins recueillis
                  </div>
                  <p className="mt-0.5 whitespace-pre-line text-sm font-medium text-codialis-navy">
                    {dossier.entretien.besoinsRecueillis}
                  </p>
                </div>
              )}
              {dossier.entretien.orientation && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                    Non retenu · orientation proposée
                  </div>
                  <p className="mt-0.5 text-sm font-medium text-codialis-navy">
                    {libelleOrientation(dossier.entretien.orientation, dossier.referentielFiche)}
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-100 bg-white p-4">
          <h3 className="font-display text-sm font-bold text-codialis-navy">
            Pièces justificatives · {dossier.completude.deposees}/{dossier.completude.attendues}{' '}
            déposées, {dossier.completude.validees} validées
          </h3>
          <ul className="mt-3 divide-y divide-gray-50">
            {dossier.pieces.map((piece) => (
              <li
                key={piece.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
              >
                <span className="text-codialis-navy">
                  {piece.libelle}
                  {piece.obligatoire ? '' : ' (facultative)'}
                </span>
                <Pastille
                  texte={PASTILLE_PIECE[piece.statut].texte}
                  ton={PASTILLE_PIECE[piece.statut].ton}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modale>
  );
}
